import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Box,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Avatar,
  alpha,
  useTheme,
  Skeleton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Zoom,
  Slide,
  Grow,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchBinBatch,
  fetchDryingData,
  fetchDryingThunk,
  fetchDryingLineData,
} from "../../store/BinBatches/thunk";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import InventoryIcon from "@mui/icons-material/Inventory";
import BatchPredictionIcon from "@mui/icons-material/BatchPrediction";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import DryIcon from "@mui/icons-material/Dry";
import TableChartIcon from "@mui/icons-material/TableChart";
import { motion } from "framer-motion";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  ReferenceLine,
} from "recharts";

const DRYING_LINE_COLUMNS = [
  "SERIALNO",
  "BATCHNO",
  "INTAKEMOISTURE",
  "SOWINGDATE",
  "HARVESTSTARTTIME",
  "HARVESTENDTIME",
  "DISPATCHTIME",
  "GATEENTRYTIME",
  "UNLOADINGENDTIME",
  "DRYINGSTARTDATETIME",
  "DRYINGENDTIME",
  "SHELLINGSTARTTIME",
  "SHELLINGENDTIME",
];

const COLUMN_DISPLAY_NAMES = {
  SERIALNO: "SERIAL NO",
  BATCHNO: "BATCH NO",
  INTAKEMOISTURE: "INTAKE MOISTURE",
  SOWINGDATE: "SOWING DATE",
  HARVESTSTARTTIME: "HARVEST START",
  HARVESTENDTIME: "HARVEST END",
  DISPATCHTIME: "DISPATCH TIME",
  GATEENTRYTIME: "ARRIVED",
  UNLOADINGENDTIME: "UNLOADING END",
  DRYINGSTARTDATETIME: "DRYING START",
  DRYINGENDTIME: "DRYING END",
  SHELLINGSTARTTIME: "SHELLING START",
  SHELLINGENDTIME: "SHELLING END",
};

const AnimatedStatCard = ({
  title,
  value,
  icon,
  color,
  gradient,
  onClick,
  loading,
  trend,
}) => (
  <Grow in timeout={300}>
    <Card
      onClick={onClick}
      sx={{
        cursor: onClick ? "pointer" : "default",
        height: "100%",
        borderRadius: 3,
        overflow: "hidden",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": onClick
          ? { transform: "translateY(-8px) scale(1.02)", boxShadow: 10 }
          : {},
        position: "relative",
        background: `linear-gradient(135deg, ${alpha(color, 0.05)} 0%, ${alpha(color, 0.02)} 100%)`,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: gradient,
        }}
      />
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, letterSpacing: 1 }}
            >
              {title}
            </Typography>
            {loading ? (
              <Skeleton variant="text" width="80%" height={45} sx={{ mt: 1 }} />
            ) : (
              <Typography
                variant="h3"
                sx={{ fontWeight: 800, mt: 1, color: color }}
              >
                {value || 0}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: alpha(color, 0.15), width: 56, height: 56 }}>
            {React.cloneElement(icon, { sx: { fontSize: 32, color: color } })}
          </Avatar>
        </Box>
        {trend && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
            <TrendingUpIcon sx={{ fontSize: 14, color: "success.main" }} />
            <Typography variant="caption" color="success.main">
              {trend}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  </Grow>
);

const LoadingSkeleton = () => (
  <Box sx={{ p: 3 }}>
    <Skeleton
      variant="rectangular"
      height={200}
      sx={{ borderRadius: 3, mb: 3 }}
    />
    <Stack direction="row" spacing={2}>
      <Skeleton variant="rounded" width="33%" height={120} />
      <Skeleton variant="rounded" width="33%" height={120} />
      <Skeleton variant="rounded" width="33%" height={120} />
    </Stack>
  </Box>
);

const BinBatches = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const containerRef = useRef(null);

  const getUrlParams = useCallback(() => {
    try {
      const fullUrl = window.location.href;
      console.log("🔍 Full URL:", fullUrl);

      const hash = window.location.hash;
      let queryString = "";

      if (hash && hash.includes("?")) {
        queryString = hash.split("?")[1];
      }

      console.log("🔍 Query String:", queryString);

      if (!queryString) {
        console.warn("⚠️ No query string found in URL");
        return {};
      }

      const params = new URLSearchParams(queryString);

      const getParam = (key) => {
        const value = params.get(key);
        if (!value) return "";
        try {
          return decodeURIComponent(value);
        } catch (e) {
          return value;
        }
      };

      const extractedParams = {
        userId: getParam("userId"),
        basicauth: getParam("basicauth"),
        password: getParam("password"),
        organization: getParam("organization"),
        season: getParam("season"),
        typeOfDryer: getParam("typeOfDryer"),
        binNo: getParam("binNo"),
        cycleNo: getParam("cycleNo"),
        orgId: getParam("orgId"),
        warehouseId: getParam("warehouseId"),
      };

      console.log("✅ Extracted Params:", {
        ...extractedParams,
        password: extractedParams.password ? "***" : "",
      });

      return extractedParams;
    } catch (error) {
      console.error("❌ Error parsing URL:", error);
      return {};
    }
  }, []);

  const urlParams = getUrlParams();

  const [userId, setUserId] = useState(() => {
    const id = urlParams.userId || sessionStorage.getItem("userId") || "200";
    console.log("✅ Initial userId:", id);
    return id;
  });

  const [username, setUsername] = useState(() => {
    return urlParams.username || sessionStorage.getItem("username") || "Guest";
  });

  const [password, setPassword] = useState(() => {
    return urlParams.password || sessionStorage.getItem("password") || "";
  });

  useEffect(() => {
    if (userId) {
      sessionStorage.setItem("userId", userId);
      console.log("💾 Saved userId to sessionStorage:", userId);
    }
    if (username) {
      sessionStorage.setItem("username", username);
    }
    if (password) {
      sessionStorage.setItem("password", password);
    }
  }, [userId, username, password]);

  useEffect(() => {
    const handleUrlChange = () => {
      const newParams = getUrlParams();
      console.log("🔄 URL changed, new params:", newParams);

      let updated = false;

      if (newParams.userId && newParams.userId !== userId) {
        console.log(
          "🔄 Updating userId from:",
          userId,
          "to:",
          newParams.userId,
        );
        setUserId(newParams.userId);
        updated = true;
      }

      if (newParams.username && newParams.username !== username) {
        setUsername(newParams.username);
        sessionStorage.setItem("username", newParams.username);
      }

      if (newParams.password && newParams.password !== password) {
        setPassword(newParams.password);
        sessionStorage.setItem("password", newParams.password);
      }

      if (updated && newParams.userId) {
        console.log("🔄 Refetching data for new userId:", newParams.userId);
        dispatch(fetchDryingThunk(newParams.userId));
      }
    };

    window.addEventListener("hashchange", handleUrlChange);
    window.addEventListener("popstate", handleUrlChange);
    handleUrlChange();

    return () => {
      window.removeEventListener("hashchange", handleUrlChange);
      window.removeEventListener("popstate", handleUrlChange);
    };
  }, [dispatch, getUrlParams, userId, username, password]);

  const binBatchState = useSelector(
    (state) => state.binBatch?.binBatch || state.binBatch,
  );
  const dryingState = useSelector(
    (state) => state.binBatch?.drying || state.drying,
  );
  console.log(dryingState);
  const dryingLineState = useSelector(
    (state) => state.binBatch?.dryingLine || state.dryingLine,
  );

  const binBatchData = binBatchState?.data || [];
  const binBatchLoading = binBatchState?.loading || false;
  const binBatchError = binBatchState?.error || null;

  const rawDryingData = dryingState?.data || [];
  const dryingLoading = dryingState?.loading || false;
  const dryingError = dryingState?.error || null;

  const dryingLineData = dryingLineState?.data || [];
  const dryingLineLoading = dryingLineState?.loading || false;
  const dryingLineError = dryingLineState?.error || null;

  const [selectedOrganization, setSelectedOrganization] = useState("");
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [selectedWarehouseId, setSelectedWarehouseId] = useState("");
  const [selectedSeason, setSelectedSeason] = useState("");
  const [selectedSeasonId, setSelectedSeasonId] = useState("");
  const [selectedTypeOfDryer, setSelectedTypeOfDryer] = useState("");
  const [selectedCycleNo, setSelectedCycleNo] = useState("");
  const [selectedBin, setSelectedBin] = useState("");
  const [selectedBinId, setSelectedBinId] = useState("");

  const [showBinBatchDetails, setShowBinBatchDetails] = useState(false);
  const [showDryingDetails, setShowDryingDetails] = useState(false);
  const [showDryingLineDetails, setShowDryingLineDetails] = useState(false);
  const [selectedDryingId, setSelectedDryingId] = useState(null);

  useEffect(() => {
    if (userId) {
      console.log("🚀 Fetching drying data for userId:", userId);
      dispatch(fetchDryingThunk(userId));
    } else {
      console.warn("⚠️ No userId available, skipping data fetch");
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (selectedDryingId) {
      console.log("🔄 Fetching Drying Line data for ID:", selectedDryingId);
      dispatch(fetchDryingLineData(selectedDryingId));
      setShowDryingLineDetails(true);
    }
  }, [dispatch, selectedDryingId]);

  const mapDryingLineData = useCallback((data) => {
    if (!data || data.length === 0) return [];

    return data.map((item, index) => {
      const mappedItem = {};

      DRYING_LINE_COLUMNS.forEach((column) => {
        const possibleKeys = [
          column,
          column.toLowerCase(),
          column.toUpperCase(),
          column.charAt(0).toUpperCase() + column.slice(1).toLowerCase(),
          column === "SERIALNO"
            ? ["serialNo", "serialno", "SERIALNO", "srNo", "srno"]
            : null,
          column === "BATCHNO"
            ? ["batchNo", "batchno", "BATCHNO", "batchNumber"]
            : null,
          column === "INTAKEMOISTURE"
            ? ["intakeMoisture", "intakemoisture", "INTAKEMOISTURE", "moisture"]
            : null,
          column === "SOWINGDATE"
            ? ["sowingDate", "sowingdate", "SOWINGDATE"]
            : null,
          column === "HARVESTSTARTTIME"
            ? ["harvestStartTime", "harveststarttime", "HARVESTSTARTTIME"]
            : null,
          column === "HARVESTENDTIME"
            ? ["harvestEndTime", "harvestendtime", "HARVESTENDTIME"]
            : null,
          column === "DISPATCHTIME"
            ? ["dispatchTime", "dispatchtime", "DISPATCHTIME"]
            : null,
          column === "GATEENTRYTIME"
            ? ["gateEntryTime", "gateentrytime", "GATEENTRYTIME", "arrived"]
            : null,
          column === "UNLOADINGENDTIME"
            ? ["unloadingEndTime", "unloadingendtime", "UNLOADINGENDTIME"]
            : null,
          column === "DRYINGSTARTDATETIME"
            ? [
                "dryingStartDateTime",
                "dryingstartdatetime",
                "DRYINGSTARTDATETIME",
              ]
            : null,
          column === "DRYINGENDTIME"
            ? ["dryingEndTime", "dryingendtime", "DRYINGENDTIME"]
            : null,
          column === "SHELLINGSTARTTIME"
            ? ["shellingStartTime", "shellingstarttime", "SHELLINGSTARTTIME"]
            : null,
          column === "SHELLINGENDTIME"
            ? ["shellingEndTime", "shellingendtime", "SHELLINGENDTIME"]
            : null,
        ]
          .flat()
          .filter(Boolean);

        let foundValue = null;
        for (const key of possibleKeys) {
          if (
            item[key] !== undefined &&
            item[key] !== null &&
            item[key] !== ""
          ) {
            foundValue = item[key];
            break;
          }
        }

        if (foundValue === null) {
          const itemKeys = Object.keys(item);
          for (const key of itemKeys) {
            if (key.toLowerCase() === column.toLowerCase()) {
              foundValue = item[key];
              break;
            }
          }
        }

        if (
          foundValue &&
          typeof foundValue === "string" &&
          (column.includes("DATE") ||
            column.includes("TIME") ||
            column.includes("DATETIME"))
        ) {
          try {
            const date = new Date(foundValue);
            if (!isNaN(date.getTime())) {
              foundValue = date;
            }
          } catch (e) {
          }
        }

        mappedItem[column] = foundValue;
      });

      if (!mappedItem.SERIALNO) {
        mappedItem.SERIALNO = index + 1;
      }

      return mappedItem;
    });
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.hash.split('?')[1]);
    const basicauth = urlParams.get('basicauth');
    if (basicauth) {
      const decodedAuth = decodeURIComponent(basicauth);
      sessionStorage.setItem("basicauth", decodedAuth);
      console.log("✅ Stored basicauth in sessionStorage");
    }
  }, []);

  const allDryingRecords = useMemo(() => {
    if (!rawDryingData.length) return [];

    return rawDryingData.map((item) => ({
      id: item.id,
      organizationName:
        item.organization$_identifier ||
        item.organization?.name ||
        "Unknown Organization",
      organizationId: item.organization,
      warehouseName:
        item.warehouse$_identifier ||
        item.warehouse?.name ||
        item.warehouseName ||
        "Unknown Warehouse",
      warehouseId: item.warehouse || item.warehouseId,
      seasonName:
        item.season$_identifier || item.season?.seasonName || "Unknown Season",
      seasonId: item.season,
      typeOfDryerName: item.typeOfDryer || "Unknown Dryer",
      cycleNo: item.cycleno,
      binNo: item.binNo$_identifier || item.binNo || item.binno,
      binId: item.binNo,
      temperature: item.upfronttemp || item.temperature || 0,
      recordTime: item.recordTime,
    }));
  }, [rawDryingData]);

  console.log("📊 Total drying records:", allDryingRecords.length);
  if (allDryingRecords.length > 0) {
    console.log("📊 Sample processed record:", allDryingRecords[0]);
  }

  const uniqueOrganizations = useMemo(() => {
    const orgMap = new Map();
    allDryingRecords.forEach((record) => {
      if (
        record.organizationName &&
        record.organizationName !== "Unknown Organization" &&
        !orgMap.has(record.organizationName)
      ) {
        orgMap.set(record.organizationName, {
          id: record.organizationId,
          name: record.organizationName,
        });
      }
    });
    const result = Array.from(orgMap.values());
    console.log(
      "✅ Unique Organizations:",
      result.map((o) => o.name),
    );
    return result;
  }, [allDryingRecords]);

  const uniqueWarehouses = useMemo(() => {
    if (!selectedOrganization) return [];

    const warehouseMap = new Map();
    allDryingRecords.forEach((record) => {
      if (
        record.organizationName === selectedOrganization &&
        record.warehouseName &&
        record.warehouseName !== "Unknown Warehouse" &&
        !warehouseMap.has(record.warehouseName)
      ) {
        warehouseMap.set(record.warehouseName, {
          id: record.warehouseId,
          name: record.warehouseName,
        });
      }
    });
    const result = Array.from(warehouseMap.values());
    console.log(
      `✅ Unique Warehouses for ${selectedOrganization}:`,
      result.map((w) => w.name),
    );
    return result;
  }, [allDryingRecords, selectedOrganization]);

  const uniqueSeasons = useMemo(() => {
    if (!selectedOrganization || !selectedWarehouse) return [];

    const seasonMap = new Map();
    allDryingRecords.forEach((record) => {
      if (
        record.organizationName === selectedOrganization &&
        record.warehouseName === selectedWarehouse &&
        record.seasonName &&
        record.seasonName !== "Unknown Season" &&
        !seasonMap.has(record.seasonName)
      ) {
        seasonMap.set(record.seasonName, {
          id: record.seasonId,
          name: record.seasonName,
        });
      }
    });
    const result = Array.from(seasonMap.values());
    console.log(
      `✅ Unique Seasons for ${selectedOrganization} - ${selectedWarehouse}:`,
      result.map((s) => s.name),
    );
    return result;
  }, [allDryingRecords, selectedOrganization, selectedWarehouse]);

  const uniqueTypeOfDryers = useMemo(() => {
    if (!selectedOrganization || !selectedWarehouse || !selectedSeason) return [];

    const dryerMap = new Map();
    allDryingRecords.forEach((record) => {
      if (
        record.organizationName === selectedOrganization &&
        record.warehouseName === selectedWarehouse &&
        record.seasonName === selectedSeason &&
        record.typeOfDryerName &&
        record.typeOfDryerName !== "Unknown Dryer" &&
        !dryerMap.has(record.typeOfDryerName)
      ) {
        dryerMap.set(record.typeOfDryerName, {
          id: record.typeOfDryerName,
          name: record.typeOfDryerName,
        });
      }
    });
    const result = Array.from(dryerMap.values());
    console.log(
      `✅ Unique Type of Dryers:`,
      result.map((d) => d.name),
    );
    return result;
  }, [allDryingRecords, selectedOrganization, selectedWarehouse, selectedSeason]);

  const uniqueCycles = useMemo(() => {
    if (!selectedOrganization || !selectedWarehouse || !selectedSeason || !selectedTypeOfDryer)
      return [];

    const cycleSet = new Set();
    allDryingRecords.forEach((record) => {
      if (
        record.organizationName === selectedOrganization &&
        record.warehouseName === selectedWarehouse &&
        record.seasonName === selectedSeason &&
        record.typeOfDryerName === selectedTypeOfDryer &&
        record.cycleNo
      ) {
        cycleSet.add(record.cycleNo);
      }
    });
    const result = Array.from(cycleSet).sort((a, b) => Number(a) - Number(b));
    console.log(`✅ Unique Cycles:`, result);
    return result;
  }, [
    allDryingRecords,
    selectedOrganization,
    selectedWarehouse,
    selectedSeason,
    selectedTypeOfDryer,
  ]);

  const uniqueBins = useMemo(() => {
    if (
      !selectedOrganization ||
      !selectedWarehouse ||
      !selectedSeason ||
      !selectedTypeOfDryer ||
      !selectedCycleNo
    )
      return [];

    const binMap = new Map();
    allDryingRecords.forEach((record) => {
      if (
        record.organizationName === selectedOrganization &&
        record.warehouseName === selectedWarehouse &&
        record.seasonName === selectedSeason &&
        record.typeOfDryerName === selectedTypeOfDryer &&
        record.cycleNo === selectedCycleNo &&
        record.binNo &&
        !binMap.has(record.binNo)
      ) {
        binMap.set(record.binNo, {
          id: record.binId,
          binno: record.binNo,
        });
      }
    });
    const result = Array.from(binMap.values()).sort((a, b) => {
      const numA = parseInt(a.binno);
      const numB = parseInt(b.binno);
      return numA - numB;
    });
    console.log(
      `✅ Unique Bins (ascending):`,
      result.map((b) => b.binno),
    );
    return result;
  }, [
    allDryingRecords,
    selectedOrganization,
    selectedWarehouse,
    selectedSeason,
    selectedTypeOfDryer,
    selectedCycleNo,
  ]);

  const filteredDryingData = useMemo(() => {
    if (
      !selectedOrganization ||
      !selectedWarehouse ||
      !selectedSeason ||
      !selectedTypeOfDryer ||
      !selectedCycleNo ||
      !selectedBin
    ) {
      return [];
    }

    const filtered = allDryingRecords.filter(
      (record) =>
        record.organizationName === selectedOrganization &&
        record.warehouseName === selectedWarehouse &&
        record.seasonName === selectedSeason &&
        record.typeOfDryerName === selectedTypeOfDryer &&
        record.cycleNo === selectedCycleNo &&
        record.binNo === selectedBin,
    );

    return filtered.sort((a, b) => {
      if (a.recordTime && b.recordTime) {
        return new Date(a.recordTime) - new Date(b.recordTime);
      }
      return (a.id || "").localeCompare(b.id || "");
    });
  }, [
    allDryingRecords,
    selectedOrganization,
    selectedWarehouse,
    selectedSeason,
    selectedTypeOfDryer,
    selectedCycleNo,
    selectedBin,
  ]);

  const chartData = useMemo(() => {
    if (!dryingLineData || dryingLineData.length === 0) {
      console.log("⚠️ No drying line data available for chart");
      return [];
    }

    console.log("📊 Processing drying line data for chart:", dryingLineData);

    const sortedData = [...dryingLineData].sort((a, b) => {
      const serialA = Number(a.serialNo) || 0;
      const serialB = Number(b.serialNo) || 0;
      return serialA - serialB;
    });

    console.log("📊 Sorted drying line data by serialNo:", sortedData);

    return sortedData.map((item, index) => {
      const batchNo =
        item.batchNo ||
        item.batchno ||
        item.BatchNo ||
        item.BATCHNO ||
        item.batch_no ||
        `Batch ${index + 1}`;

      const parseDate = (value) => {
        if (!value) return null;
        try {
          const date = new Date(value);
          return isNaN(date.getTime()) ? null : date;
        } catch (e) {
          console.warn("Failed to parse date:", value);
          return null;
        }
      };

      const sowingDate = parseDate(
        item.sowingdate || item.SOWINGDATE || item.sowingDate,
      );
      const gateEntryTime = parseDate(
        item.gateentrytime ||
          item.GATEENTRYTIME ||
          item.gateEntryTime ||
          item.gateEntryTime,
      );
      const harvestEndTime = parseDate(
        item.harvestendtime || item.HARVESTENDTIME || item.harvestEndTime,
      );
      const dispatchTime = parseDate(
        item.dispatchtime || item.DISPATCHTIME || item.dispatchTime,
      );
      const unloadingEndTime = parseDate(
        item.unloadingendtime || item.UNLOADINGENDTIME || item.unloadingEndTime,
      );
      const dryingStartDateTime = parseDate(
        item.dryingstartdatetime ||
          item.DRYINGSTARTDATETIME ||
          item.dryingStartDateTime,
      );
      const shellingStartTime = parseDate(
        item.shellingstarttime ||
          item.SHELLINGSTARTTIME ||
          item.shellingStartTime,
      );

      let intakeMoisture = 0;

      if (
        item.intakemoisture !== undefined &&
        item.intakemoisture !== null &&
        item.intakemoisture !== ""
      ) {
        intakeMoisture = parseFloat(item.intakemoisture);
        console.log(`✅ Found intakemoisture for ${batchNo}: ${intakeMoisture}`);
      } else if (
        item.intakeMoisture !== undefined &&
        item.intakeMoisture !== null &&
        item.intakeMoisture !== ""
      ) {
        intakeMoisture = parseFloat(item.intakeMoisture);
        console.log(`✅ Found intakeMoisture for ${batchNo}: ${intakeMoisture}`);
      } else if (
        item.INTAKEMOISTURE !== undefined &&
        item.INTAKEMOISTURE !== null &&
        item.INTAKEMOISTURE !== ""
      ) {
        intakeMoisture = parseFloat(item.INTAKEMOISTURE);
        console.log(`✅ Found INTAKEMOISTURE for ${batchNo}: ${intakeMoisture}`);
      } else if (
        item.IntakeMoisture !== undefined &&
        item.IntakeMoisture !== null &&
        item.IntakeMoisture !== ""
      ) {
        intakeMoisture = parseFloat(item.IntakeMoisture);
        console.log(`✅ Found IntakeMoisture for ${batchNo}: ${intakeMoisture}`);
      } else if (
        item.moisture !== undefined &&
        item.moisture !== null &&
        item.moisture !== ""
      ) {
        intakeMoisture = parseFloat(item.moisture);
        console.log(`✅ Found moisture for ${batchNo}: ${intakeMoisture}`);
      } else {
        console.log(`⚠️ No moisture found for ${batchNo}`);
      }

      if (intakeMoisture === 0) {
        Object.keys(item).forEach((key) => {
          if (
            key.toLowerCase().includes("moisture") &&
            item[key] !== null &&
            item[key] !== undefined &&
            item[key] !== ""
          ) {
            const val = parseFloat(item[key]);
            if (!isNaN(val) && val > 0) {
              intakeMoisture = val;
              console.log(
                `✅ Found moisture in field '${key}' for ${batchNo}: ${intakeMoisture}`,
              );
            }
          }
        });
      }

      if (isNaN(intakeMoisture) || intakeMoisture < 0) {
        intakeMoisture = 0;
      }

      const serialNo = Number(item.serialNo) || index + 1;

      return {
        batchNo: String(batchNo),
        serialNo: serialNo,
        sowingDate: sowingDate,
        gateEntryTime: gateEntryTime,
        harvestEndTime: harvestEndTime,
        dispatchTime: dispatchTime,
        unloadingEndTime: unloadingEndTime,
        dryingStartDateTime: dryingStartDateTime,
        shellingStartTime: shellingStartTime,
        sowingDateDisplay: sowingDate ? sowingDate.toLocaleString() : null,
        gateEntryTimeDisplay: gateEntryTime
          ? gateEntryTime.toLocaleString()
          : null,
        harvestEndTimeDisplay: harvestEndTime
          ? harvestEndTime.toLocaleString()
          : null,
        dispatchTimeDisplay: dispatchTime
          ? dispatchTime.toLocaleString()
          : null,
        unloadingEndTimeDisplay: unloadingEndTime
          ? unloadingEndTime.toLocaleString()
          : null,
        dryingStartDateTimeDisplay: dryingStartDateTime
          ? dryingStartDateTime.toLocaleString()
          : null,
        shellingStartTimeDisplay: shellingStartTime
          ? shellingStartTime.toLocaleString()
          : null,
        intakeMoisture: intakeMoisture,
        _originalDate: item.createdAt || item.recordTime || item.date,
      };
    });
  }, [dryingLineData]);

  console.log("📊 Chart Data prepared:", chartData);
  console.log("📊 Chart Data length:", chartData.length);

  const dateRange = useMemo(() => {
    if (!chartData || chartData.length === 0) return { min: null, max: null };

    const allDates = [];
    const dateFields = [
      "sowingDate",
      "gateEntryTime",
      "harvestEndTime",
      "dispatchTime",
      "unloadingEndTime",
      "dryingStartDateTime",
      "shellingStartTime",
    ];

    chartData.forEach((item) => {
      dateFields.forEach((field) => {
        const dateValue = item[field];
        if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
          allDates.push(dateValue.getTime());
        }
      });
    });

    if (allDates.length === 0) {
      console.log("⚠️ No valid dates found in chart data");
      return { min: null, max: null };
    }

    const minDate = new Date(Math.min(...allDates));
    const maxDate = new Date(Math.max(...allDates));

    const range = maxDate.getTime() - minDate.getTime();
    const padding = range * 0.05 || 86400000;
    const paddedMin = new Date(minDate.getTime() - padding);
    const paddedMax = new Date(maxDate.getTime() + padding);

    console.log("📊 Date Range:", { min: paddedMin, max: paddedMax });
    return { min: paddedMin, max: paddedMax };
  }, [chartData]);

  const moistureRange = useMemo(() => {
    if (!chartData || chartData.length === 0) return { min: 0, max: 100 };

    const moistureValues = chartData
      .map((item) => item.intakeMoisture)
      .filter((val) => !isNaN(val) && val > 0);

    console.log("📊 Moisture Values found:", moistureValues);

    if (moistureValues.length === 0) {
      console.log("⚠️ No valid moisture values found");
      return { min: 0, max: 100 };
    }

    const minMoisture = Math.min(...moistureValues);
    const maxMoisture = Math.max(...moistureValues);

    const range = maxMoisture - minMoisture;
    const padding = range * 0.1 || 5;
    const paddedMin = Math.max(0, Math.floor(minMoisture - padding));
    const paddedMax = Math.ceil(maxMoisture + padding);

    console.log("📊 Moisture Range:", { min: paddedMin, max: paddedMax });
    return { min: paddedMin, max: paddedMax };
  }, [chartData]);

  useEffect(() => {
    setSelectedWarehouse("");
    setSelectedWarehouseId("");
    setSelectedSeason("");
    setSelectedSeasonId("");
    setSelectedTypeOfDryer("");
    setSelectedCycleNo("");
    setSelectedBin("");
    setSelectedBinId("");
    setShowBinBatchDetails(false);
    setShowDryingDetails(false);
    setShowDryingLineDetails(false);
    setSelectedDryingId(null);
  }, [selectedOrganization]);

  useEffect(() => {
    setSelectedSeason("");
    setSelectedSeasonId("");
    setSelectedTypeOfDryer("");
    setSelectedCycleNo("");
    setSelectedBin("");
    setSelectedBinId("");
    setShowBinBatchDetails(false);
    setShowDryingDetails(false);
    setShowDryingLineDetails(false);
    setSelectedDryingId(null);
  }, [selectedWarehouse]);

  useEffect(() => {
    setSelectedTypeOfDryer("");
    setSelectedCycleNo("");
    setSelectedBin("");
    setSelectedBinId("");
    setShowBinBatchDetails(false);
    setShowDryingDetails(false);
    setShowDryingLineDetails(false);
    setSelectedDryingId(null);
  }, [selectedSeason]);

  useEffect(() => {
    setSelectedCycleNo("");
    setSelectedBin("");
    setSelectedBinId("");
    setShowBinBatchDetails(false);
    setShowDryingDetails(false);
    setShowDryingLineDetails(false);
    setSelectedDryingId(null);
  }, [selectedTypeOfDryer]);

  useEffect(() => {
    setSelectedBin("");
    setSelectedBinId("");
    setShowBinBatchDetails(false);
    setShowDryingDetails(false);
    setShowDryingLineDetails(false);
    setSelectedDryingId(null);
  }, [selectedCycleNo]);

  useEffect(() => {
    if (
      selectedOrganization &&
      selectedWarehouse &&
      selectedSeason &&
      selectedTypeOfDryer &&
      selectedCycleNo &&
      selectedBin &&
      filteredDryingData.length > 0
    ) {
      console.log("📊 Selected Records:", filteredDryingData);

      const firstRecordId = filteredDryingData[0]?.id;
      if (firstRecordId) {
        console.log("🎯 Setting Drying ID for line data:", firstRecordId);
        setSelectedDryingId(firstRecordId);
      }

      filteredDryingData.forEach((record) => {
        console.log("Drying ID:", record.id);
      });
    }
  }, [
    filteredDryingData,
    selectedOrganization,
    selectedWarehouse,
    selectedSeason,
    selectedTypeOfDryer,
    selectedCycleNo,
    selectedBin,
  ]);

  const handleOrganizationChange = (event) => {
    const orgName = event.target.value;
    const org = uniqueOrganizations.find((o) => o.name === orgName);
    setSelectedOrganization(orgName);
    setSelectedOrganizationId(org?.id || "");
  };

  const handleWarehouseChange = (event) => {
    const warehouseName = event.target.value;
    const warehouse = uniqueWarehouses.find((w) => w.name === warehouseName);
    setSelectedWarehouse(warehouseName);
    setSelectedWarehouseId(warehouse?.id || "");
  };

  const handleSeasonChange = (event) => {
    const seasonName = event.target.value;
    const season = uniqueSeasons.find((s) => s.name === seasonName);
    setSelectedSeason(seasonName);
    setSelectedSeasonId(season?.id || "");
  };

  const handleTypeOfDryerChange = (event) => {
    setSelectedTypeOfDryer(event.target.value);
  };

  const handleCycleChange = (event) => {
    setSelectedCycleNo(event.target.value);
  };

  const handleBinChange = (event) => {
    const binNo = event.target.value;
    const bin = uniqueBins.find((b) => b.binno === binNo);
    setSelectedBin(binNo);
    setSelectedBinId(bin?.id || "");
  };

  const handleFetchBinBatch = () => {
    if (
      selectedOrganization &&
      selectedWarehouse &&
      selectedSeason &&
      selectedTypeOfDryer &&
      selectedCycleNo &&
      selectedBin &&
      userId
    ) {
      dispatch(
        fetchBinBatch(
          userId,
          selectedOrganizationId,
          selectedSeasonId,
          selectedBinId,
          selectedCycleNo,
          selectedTypeOfDryer,
        ),
      );
      setShowBinBatchDetails(true);
      setShowDryingDetails(false);
      setShowDryingLineDetails(false);
    } else {
      alert(
        "Please complete all selections: Organization, Warehouse, Season, Type of Dryer, Cycle No, and Bin",
      );
    }
  };

  const handleFetchDrying = () => {
    if (
      selectedOrganization &&
      selectedWarehouse &&
      selectedSeason &&
      selectedTypeOfDryer &&
      selectedCycleNo &&
      selectedBin &&
      userId
    ) {
      dispatch(
        fetchDryingData(
          userId,
          selectedOrganizationId,
          selectedSeasonId,
          selectedBinId,
          selectedCycleNo,
          selectedTypeOfDryer,
        ),
      );
      setShowDryingDetails(true);
      setShowBinBatchDetails(false);
      setShowDryingLineDetails(false);
    } else {
      alert(
        "Please complete all selections: Organization, Warehouse, Season, Type of Dryer, Cycle No, and Bin",
      );
    }
  };

  const handleClearAll = () => {
    setSelectedOrganization("");
    setSelectedOrganizationId("");
    setSelectedWarehouse("");
    setSelectedWarehouseId("");
    setSelectedSeason("");
    setSelectedSeasonId("");
    setSelectedTypeOfDryer("");
    setSelectedCycleNo("");
    setSelectedBin("");
    setSelectedBinId("");
    setShowBinBatchDetails(false);
    setShowDryingDetails(false);
    setShowDryingLineDetails(false);
    setSelectedDryingId(null);
  };

  const mappedDryingLineData = useMemo(() => {
    const mappedData = mapDryingLineData(dryingLineData);
    return mappedData.sort((a, b) => {
      const serialA = Number(a.SERIALNO) || 0;
      const serialB = Number(b.SERIALNO) || 0;
      return serialA - serialB;
    });
  }, [dryingLineData, mapDryingLineData]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataItem = payload[0]?.payload;

      return (
        <Paper
          sx={{
            p: 2,
            bgcolor: "background.paper",
            boxShadow: 3,
            maxWidth: 300,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
            Batch: {label}
          </Typography>
          {payload.map((entry, index) => {
            const isMoisture = entry.dataKey === "intakeMoisture";

            let displayValue = entry.value;
            if (!isMoisture && dataItem) {
              const displayKey = entry.dataKey + "Display";
              if (dataItem[displayKey]) {
                displayValue = dataItem[displayKey];
              } else if (entry.value instanceof Date) {
                displayValue = entry.value.toLocaleString();
              }
            }

            return (
              <Box
                key={index}
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: entry.color,
                  }}
                />
                <Typography variant="caption" sx={{ minWidth: 100 }}>
                  {entry.name}:
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                  {isMoisture ? `${entry.value}%` : displayValue || "N/A"}
                </Typography>
              </Box>
            );
          })}
        </Paper>
      );
    }
    return null;
  };

  const shouldRenderChart =
    showDryingLineDetails &&
    chartData.length > 0 &&
    dateRange.min &&
    dateRange.max;

  if (dryingLoading && !rawDryingData.length) {
    return <LoadingSkeleton />;
  }

  console.log("🔄 Chart Render Conditions:", {
    showDryingLineDetails,
    chartDataLength: chartData.length,
    hasDateRange: !!(dateRange.min && dateRange.max),
    shouldRenderChart,
    moistureRange,
  });

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        mt: 3, 
        mb: 2,
        height: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        scrollBehavior: 'smooth',
        
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: alpha(theme.palette.primary.main, 0.05),
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: alpha(theme.palette.primary.main, 0.3),
          borderRadius: '4px',
          '&:hover': {
            background: alpha(theme.palette.primary.main, 0.5),
          },
        },
      }}
      ref={containerRef}
    >
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Paper
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            transition: 'box-shadow 0.3s ease',
          }}
          elevation={1}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}
            >
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                  width: 64,
                  height: 64,
                  flexShrink: 0,
                }}
              >
                <InventoryIcon
                  sx={{ fontSize: 36, color: theme.palette.primary.main }}
                />
              </Avatar>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Bin Batches Management
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 1, mb: 1 }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '16px' }}>Organization *</InputLabel>
                  <Select
                    value={selectedOrganization}
                    onChange={handleOrganizationChange}
                    label="Organization *"
                    size="small"
                    sx={{ 
                      fontSize: '16px', 
                      height: '32px',
                      '& .MuiSelect-select': { py: 0.5 }
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '16px' }}>-- Select --</MenuItem>
                    {uniqueOrganizations.map((org) => (
                      <MenuItem key={org.id || org.name} value={org.name} sx={{ fontSize: '16px' }}>
                        <WarehouseIcon
                          fontSize="small"
                          sx={{ mr: 0.5, color: theme.palette.primary.main, fontSize: '0.875rem' }}
                        />
                        {org.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <FormControl
                  fullWidth
                  size="small"
                  disabled={!selectedOrganization}
                >
                  <InputLabel sx={{ fontSize: '16px' }}>Warehouse *</InputLabel>
                  <Select
                    value={selectedWarehouse}
                    onChange={handleWarehouseChange}
                    label="Warehouse *"
                    size="small"
                    sx={{ 
                      fontSize: '16px', 
                      height: '32px',
                      '& .MuiSelect-select': { py: 0.5 }
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '16px' }}>-- Select --</MenuItem>
                    {uniqueWarehouses.map((warehouse) => (
                      <MenuItem key={warehouse.id || warehouse.name} value={warehouse.name} sx={{ fontSize: '16px' }}>
                        <InventoryIcon
                          fontSize="small"
                          sx={{ mr: 0.5, color: theme.palette.info.main, fontSize: '0.875rem' }}
                        />
                        {warehouse.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <FormControl
                  fullWidth
                  size="small"
                  disabled={!selectedWarehouse}
                >
                  <InputLabel sx={{ fontSize: '16px' }}>Season *</InputLabel>
                  <Select
                    value={selectedSeason}
                    onChange={handleSeasonChange}
                    label="Season *"
                    size="small"
                    sx={{ 
                      fontSize: '16px', 
                      height: '32px',
                      '& .MuiSelect-select': { py: 0.5 }
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '16px' }}>-- Select --</MenuItem>
                    {uniqueSeasons.map((season) => (
                      <MenuItem
                        key={season.id || season.name}
                        value={season.name}
                        sx={{ fontSize: '16px' }}
                      >
                        <BatchPredictionIcon
                          fontSize="small"
                          sx={{ mr: 0.5, color: theme.palette.success.main, fontSize: '0.875rem' }}
                        />
                        {season.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small" disabled={!selectedSeason}>
                  <InputLabel sx={{ fontSize: '16px' }}>Dryer Type *</InputLabel>
                  <Select
                    value={selectedTypeOfDryer}
                    onChange={handleTypeOfDryerChange}
                    label="Dryer Type *"
                    size="small"
                    sx={{ 
                      fontSize: '16px', 
                      height: '32px',
                      '& .MuiSelect-select': { py: 0.5 }
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '16px' }}>-- Select --</MenuItem>
                    {uniqueTypeOfDryers.map((dryer) => (
                      <MenuItem key={dryer.id || dryer.name} value={dryer.name} sx={{ fontSize: '16px' }}>
                        <DryIcon
                          fontSize="small"
                          sx={{ mr: 0.5, color: theme.palette.secondary.main, fontSize: '0.875rem' }}
                        />
                        {dryer.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <FormControl
                  fullWidth
                  size="small"
                  disabled={!selectedTypeOfDryer}
                >
                  <InputLabel sx={{ fontSize: '16px' }}>Cycle No *</InputLabel>
                  <Select
                    value={selectedCycleNo}
                    onChange={handleCycleChange}
                    label="Cycle No *"
                    size="small"
                    sx={{ 
                      fontSize: '16px', 
                      height: '32px',
                      '& .MuiSelect-select': { py: 0.5 }
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '16px' }}>-- Select --</MenuItem>
                    {uniqueCycles.map((cycle) => (
                      <MenuItem key={cycle} value={cycle} sx={{ fontSize: '16px' }}>
                        <AgricultureIcon
                          fontSize="small"
                          sx={{ mr: 0.5, color: theme.palette.warning.main, fontSize: '0.875rem' }}
                        />
                        Cycle {cycle}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small" disabled={!selectedCycleNo}>
                  <InputLabel sx={{ fontSize: '16px' }}>Bin *</InputLabel>
                  <Select
                    value={selectedBin}
                    onChange={handleBinChange}
                    label="Bin *"
                    size="small"
                    sx={{ 
                      fontSize: '16px', 
                      height: '32px',
                      '& .MuiSelect-select': { py: 0.5 }
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '16px' }}>-- Select --</MenuItem>
                    {uniqueBins.map((bin) => (
                      <MenuItem key={bin.id || bin.binno} value={bin.binno} sx={{ fontSize: '16px' }}>
                        <InventoryIcon
                          fontSize="small"
                          sx={{ mr: 0.5, color: theme.palette.info.main, fontSize: '0.875rem' }}
                        />
                        Bin {bin.binno}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </motion.div>

      {showBinBatchDetails && binBatchData.length > 0 && (
        <Slide direction="up" in mountOnEnter unmountOnExit>
          <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <InventoryIcon color="primary" /> Bin Batch Details
            </Typography>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}
                  >
                    {Object.keys(binBatchData[0] || {}).map((key) => (
                      <TableCell key={key}>
                        <strong>{key.replace(/_/g, " ").toUpperCase()}</strong>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {binBatchData.map((item, idx) => (
                    <TableRow key={idx} hover>
                      {Object.values(item).map((value, colIdx) => (
                        <TableCell key={colIdx}>
                          {value instanceof Date
                            ? value.toLocaleDateString()
                            : typeof value === "object"
                              ? JSON.stringify(value)
                              : value || "-"}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Slide>
      )}

      {shouldRenderChart ? (
        <Slide direction="up" in mountOnEnter unmountOnExit>
          <Paper sx={{ p: 1, borderRadius: 3, mt: 2 ,mb:3}}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <TrendingUpIcon color="primary" /> Bin Batches Management Graph
            </Typography>
             <Box
              sx={{
                justifyContent: "center",
                borderRadius: 2,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 2,
                fontWeight:700,
              }}
            >
              {[
                { color: theme.palette.primary.main, label: 'Intake Moisture' },
                { color: '#8105af', label: 'Sowing Date' },
                { color: '#f4b028', label: 'Harvest End' }, 
                { color: '#313388', label: 'Dispatch' },
                { color: '#036d2c', label: 'Arrived' },
                { color: '#f7e349', label: 'Unloading End' },
                { color: '#fa5454', label: 'Drying Start' },
                { color: '#ef61fa', label: 'Shelling Start' },
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      bgcolor: item.color,
                      borderRadius: item.label.includes('Moisture') ? 0 : '50%',
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box> 
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart
                data={chartData}
                margin={{ top: 10, right: 60, left: 60, bottom: 15 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                
                <XAxis
                  dataKey="batchNo"
                  interval={0}
                  height={70}
                  tick={{
                    fontSize: 12,
                    angle: -50,
                    textAnchor: "end",
                  }}
                  label={{
                    value: "Batch Number",
                    position: "insideBottom",
                    offset: -10,
                    style: {
                      fontWeight: "bold",
                      fill: theme.palette.text.primary,
                    },
                  }}
                />
                <YAxis 
                  yAxisId="left"
                  domain={[dateRange.min.getTime(), dateRange.max.getTime()]}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return date.toLocaleDateString();
                  }}
                  label={{ 
                    value: 'Date & Time', 
                    angle: -90, 
                    position: 'insideLeft',
                    offset: -20,
                    dy:15,
                    style: { fontWeight: 'bold', fill: theme.palette.text.primary }
                  }}
                  tick={{ fontSize: 11 }}
                  scale="time"
                />
                
                <YAxis 
                  yAxisId="right" 
                  orientation="right"
                  domain={[moistureRange.min, moistureRange.max]}
                  label={{ 
                    value: 'Intake Moisture (%)', 
                    angle: 90, 
                    position: 'middle',
                    dx: 13, 
                    dy: 0, 
                    style: { 
                      fontWeight: 'bold', 
                      fill: theme.palette.primary.main,
                      textAnchor: 'middle'
                    }
                  }}
                  tick={{ fontSize: 11 }}
                />
                
                <RechartsTooltip content={<CustomTooltip />} />
                
                <Legend 
                  height={30}
                  wrapperStyle={{ fontWeight: 600 , display:"none"}}
                />
                
                <Bar
                  yAxisId="right"
                  dataKey="intakeMoisture"
                  fill={theme.palette.primary.main}
                  fillOpacity={0.6}
                  name="Intake Moisture"
                  radius={[4, 4, 0, 0]}
                  barSize={30}
                />
              
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="sowingDate"
                  stroke="#8105af"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Sowing Date"
                  connectNulls
                />
                
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="gateEntryTime"
                  stroke="#036d2c"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Arrived"
                  connectNulls
                />
                
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="harvestEndTime"
                  stroke="#f4b028"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Harvest End"
                  connectNulls
                />
                
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="dispatchTime"
                  stroke="#313388"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Dispatch Time"
                  connectNulls
                />
                
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="unloadingEndTime"
                  stroke="#f7e349"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Unloading End"
                  connectNulls
                />
                
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="dryingStartDateTime"
                  stroke="#fa5454"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Drying Start"
                  connectNulls
                />
                
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="shellingStartTime"
                  stroke="#ef61fa"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Shelling Start"
                  connectNulls
                />
                
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>
        </Slide>
      ) : (
        showDryingLineDetails && (
          <Alert severity="info" sx={{ mt: 3 }}>
            {chartData.length === 0 
              ? "No drying line data available to display chart. Please fetch drying line data first." 
              : "Unable to display chart. Please check if the data contains valid dates and moisture values."}
          </Alert>
        )
      )}
      {showDryingLineDetails && dryingLineData.length > 0 && (
        <Slide direction="up" in mountOnEnter unmountOnExit>
          <Paper
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              maxHeight: "380px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <TableChartIcon color="primary" /> Drying Line Details
              </Typography>

              {dryingLineData.length > 0 && (
                <Box
                  sx={{
                    p: 1,
                    bgcolor: alpha(theme.palette.success.main, 0.08),
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Total Records: {dryingLineData.length}
                  </Typography>
                </Box>
              )}
            </Box>

            {dryingLineLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                <CircularProgress />
              </Box>
            ) : dryingLineError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                Error loading drying line data: {dryingLineError}
              </Alert>
            ) : dryingLineData.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                No drying line data found for ID: {selectedDryingId}
              </Alert>
            ) : (
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table stickyHeader size="small">
                  <TableHead
                    sx={{
                      zIndex: 1000,
                      position: "sticky",
                      top: 0,
                    }}
                  >
                    <TableRow
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}
                    >
                      {DRYING_LINE_COLUMNS.map((column) => (
                        <TableCell
                          key={column}
                          sx={{
                            whiteSpace: "nowrap",
                            minWidth:
                              column === "SERIALNO"
                                ? "80px"
                                : column === "BATCHNO"
                                  ? "100px"
                                  : column === "INTAKEMOISTURE"
                                    ? "130px"
                                    : column === "SOWINGDATE"
                                      ? "120px"
                                      : column === "HARVESTSTARTTIME"
                                        ? "120px"
                                        : column === "HARVESTENDTIME"
                                          ? "120px"
                                          : column === "DISPATCHTIME"
                                            ? "120px"
                                            : column === "GATEENTRYTIME"
                                              ? "100px"
                                              : column === "UNLOADINGENDTIME"
                                                ? "120px"
                                                : column ===
                                                    "DRYINGSTARTDATETIME"
                                                  ? "120px"
                                                  : column === "DRYINGENDTIME"
                                                    ? "110px"
                                                    : column ===
                                                        "SHELLINGSTARTTIME"
                                                      ? "120px"
                                                      : column ===
                                                          "SHELLINGENDTIME"
                                                        ? "120px"
                                                        : "auto",
                            padding: "8px 12px",
                            fontWeight: 700,
                            fontSize: "15px",
                            backgroundColor: "#3282f3",
                            color: "#fff",
                            position: "relative",
                            zIndex: 1100,
                          }}
                        >
                          <strong>
                            {COLUMN_DISPLAY_NAMES[column] ||
                              column.replace(/_/g, " ")}
                          </strong>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mappedDryingLineData.map((item, idx) => (
                      <TableRow key={idx} hover>
                        {DRYING_LINE_COLUMNS.map((column) => {
                          const value = item[column];

                          let displayValue = value;
                          if (value instanceof Date) {
                            displayValue = value.toLocaleString();
                          } else if (
                            typeof value === "object" &&
                            value !== null
                          ) {
                            displayValue = JSON.stringify(value);
                          } else if (
                            value === null ||
                            value === undefined ||
                            value === ""
                          ) {
                            displayValue = "-";
                          }

                          return (
                            <TableCell
                              key={column}
                              sx={{
                                whiteSpace: "nowrap",
                                padding: "8px 12px",
                                fontSize: "14px",
                              }}
                            >
                              {displayValue}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Slide>
      )}

      {showBinBatchDetails && binBatchData.length === 0 && !binBatchLoading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No bin batch data found for selected criteria.
        </Alert>
      )}

      {showDryingDetails &&
        filteredDryingData.length === 0 &&
        !dryingLoading && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No drying data found for selected criteria.
          </Alert>
        )}
    </Container>
  );
};

export default BinBatches;